import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import MenuItemCard from "@/components/shared/MenuItemCard";
import ItemDetailModal from "@/components/shared/ItemDetailModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePublicMenu, MenuItem } from "@/hooks/usePublicMenu";
import { useSessionContext } from "@/contexts/SessionContext";
import { Button } from "@/components/ui/button";

export default function ElegantHome() {
  const { t } = useLanguage();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const { session } = useSessionContext();
  const { menuItems, loading } = usePublicMenu(session.order.branchId);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);

  const popularItems = menuItems.filter((item) => item.popular);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Elegant Hero */}
        <section className="relative bg-foreground text-background overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/40 to-transparent" />
          </div>
          <div className="container mx-auto px-4 py-24 md:py-36 relative z-10 text-center">
            <div className="inline-flex items-center gap-2 text-primary mb-6">
              <Star className="w-4 h-4 fill-primary" />
              <span className="text-sm font-medium tracking-widest uppercase">
                {brand.logoType === "emoji" ? brand.logo : ""} Premium Dining
              </span>
              <Star className="w-4 h-4 fill-primary" />
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-extrabold leading-tight tracking-tight">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-background/70 max-w-xl mx-auto leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-base font-bold gap-2 px-10 py-6 rounded-full"
                onClick={() => navigate("/menu")}
              >
                {t("hero.cta")}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Signature Dishes */}
        <section className="container mx-auto px-4 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-primary text-sm font-medium tracking-widest uppercase mb-2">
              {t("featured.title")}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              Signature Collection
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-4 rounded-full" />
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border bg-card animate-pulse aspect-[3/4]"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularItems.map((item) => (
                <MenuItemCard
                  key={item.id}
                  item={item}
                  onClick={setSelectedItem}
                />
              ))}
            </div>
          )}
          <div className="text-center mt-10">
            <Button
              variant="outline"
              size="lg"
              className="rounded-full px-8 gap-2"
              onClick={() => navigate("/menu")}
            >
              View Full Menu <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </section>

        {/* Elegant CTA band */}
        <section className="bg-primary/5 border-y border-primary/10">
          <div className="container mx-auto px-4 py-16 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t("order.type")}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Choose your preferred dining experience
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                variant="outline"
                size="lg"
                className="rounded-full px-8 capitalize hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all"
                onClick={() => navigate("/menu")}
              >
                {t("hero.cta")}
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {selectedItem && (
        <ItemDetailModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      )}
    </div>
  );
}
